import {
    Dispatch,
    FC,
    ReactNode,
    SetStateAction,
    useCallback,
    useEffect,
    useState,
} from 'react';
import { createContext } from 'react';

import { fetchP2PWhiteList } from '@/Utils/socket-actions/p2p-connection/p2pApis';
import { useAppSelector } from '@/hooks/useRedux';
import { IWaitingListItem, UserWhiteListType } from '@/types/key-exchange';

interface PropsTypes {
    children?: ReactNode;
    target?: string;
}

interface IContextProps {
    activeCategory: string;
    setActiveCategory: Dispatch<SetStateAction<string>>;
    activeUserItem: UserWhiteListType | IWaitingListItem | null;
    setActiveUserItem: Dispatch<
        SetStateAction<UserWhiteListType | IWaitingListItem>
    >;
    usersList: UserWhiteListType[];
    setUsersList: Dispatch<SetStateAction<UserWhiteListType[]>>;
    searchValue: string;
    setSearchValue: Dispatch<SetStateAction<string>>;
    isFetchUsersList: boolean;
    setIsFetchUsersList: Dispatch<SetStateAction<boolean>>;
    waitingUsersList: IWaitingListItem[];
    setWaitingUsersList: Dispatch<SetStateAction<IWaitingListItem[]>>;
}

const initialContextValues: IContextProps = {
    activeCategory: 'waiting',
    activeUserItem: null,
    setActiveCategory: () => {},
    setActiveUserItem: () => {},
    usersList: [],
    setUsersList: () => {},
    searchValue: '',
    setSearchValue: () => {},
    isFetchUsersList: null,
    setIsFetchUsersList: () => {},
    waitingUsersList: [],
    setWaitingUsersList: () => {},
};

export const P2PContext = createContext<IContextProps>(initialContextValues);

export const P2PProvider: FC<PropsTypes> = ({ children, target }) => {
    const [activeCategory, setActiveCategory] = useState(
        initialContextValues.activeCategory,
    );

    const [activeUserItem, setActiveUserItem] = useState(
        initialContextValues.activeUserItem,
    );

    const [isFetchUsersList, setIsFetchUsersList] = useState(
        initialContextValues.isFetchUsersList,
    );

    const [usersList, setUsersList] = useState(initialContextValues.usersList);
    const [waitingUsersList, setWaitingUsersList] = useState<
        IWaitingListItem[]
    >(initialContextValues.waitingUsersList);

    const [searchValue, setSearchValue] = useState(
        initialContextValues.searchValue,
    );
    const waitingUsers = useAppSelector(state => state.p2pReducer.waitingList);
    const activeServer = useAppSelector(
        state => state.serverDataReducer.activeServer,
    );
    const membersList = useAppSelector(
        state => state.serverDataReducer.memberList?.[activeServer],
    );

    const getWhiteList = useCallback(async () => {
        setIsFetchUsersList(true);
        const { response } = await fetchP2PWhiteList();
        setUsersList(response);
        setIsFetchUsersList(false);
    }, []);

    const updateWaitingList = useCallback(() => {
        let users = Object.values(waitingUsers);

        users = users.map((user: IWaitingListItem) => {
            return {
                ...user,
                name: membersList[user.user_id]?.name ?? '--',
                username: membersList[user.user_id]?.username ?? '--',
                is_outside_network:
                    membersList[user.user_id]?.id &&
                    membersList[user.user_id].id === user.user_id
                        ? false
                        : true,
            };
        });
        setWaitingUsersList(users as IWaitingListItem[]);
    }, [waitingUsers, membersList]);

    useEffect(() => {
        switch (activeCategory) {
            case 'waiting':
                updateWaitingList();
                break;
            case 'blocked':
                setUsersList([]);
                break;
            default:
                getWhiteList();
                break;
        }
    }, [activeCategory, getWhiteList, updateWaitingList]);

    useEffect(() => {
        if (activeCategory === 'waiting') updateWaitingList();
    }, [waitingUsers, activeCategory, updateWaitingList]);

    // If a target device_id is provided, switch to waiting tab and preselect that user
    useEffect(() => {
        if (!target) return;

        if (activeCategory !== 'waiting') setActiveCategory('waiting');

        const targetUserRaw = (waitingUsers as any)?.[target];
        if (!targetUserRaw) return;

        const enrichedUser: IWaitingListItem = {
            ...targetUserRaw,
            name: membersList?.[targetUserRaw.user_id]?.name ?? '--',
            username: membersList?.[targetUserRaw.user_id]?.username ?? '--',
            is_outside_network:
                membersList?.[targetUserRaw.user_id]?.id &&
                membersList?.[targetUserRaw.user_id].id ===
                    targetUserRaw.user_id
                    ? false
                    : true,
        };

        setActiveUserItem(enrichedUser);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [target]);

    const contextValues: IContextProps = {
        activeCategory,
        setActiveCategory,
        activeUserItem,
        setActiveUserItem,
        usersList,
        setUsersList,
        searchValue,
        setSearchValue,
        isFetchUsersList,
        setIsFetchUsersList,
        waitingUsersList,
        setWaitingUsersList,
    };

    return (
        <P2PContext.Provider value={contextValues}>
            {children}
        </P2PContext.Provider>
    );
};
